import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, Subject, takeUntil, throwError } from 'rxjs';
import { CredentialsService } from '@auth';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, tap } from 'rxjs/operators';
import { containsDeletedCurrentUserRecord } from '@core/utils';
import { SessionDeletionService } from '@core/services';

@Injectable({
  providedIn: 'root',
})
export class ApiPrefixInterceptor implements HttpInterceptor {
  private readonly _credentialsService = inject(CredentialsService);
  private readonly _sessionDeletionService = inject(SessionDeletionService);
  private readonly _translateService = inject(TranslateService);

  private readonly _ongoingRequests = new Map<string, Subject<any>>();

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If the request has the 'noauth' header, don't add the Authorization header
    if (request.headers.get('noauth')) {
      return next.handle(request);
    }

    let headers = request.headers;
    const { token } = this._credentialsService.credentials || {};
    const currentLang = this._translateService.currentLang.substring(0, 2);

    if (token) {
      if (!(request.body instanceof FormData)) {
        headers = headers.set('content-type', 'application/json');
      }
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    if (!request.url.includes('profile-photo') && !request.url.includes('member-photo')) {
      headers = headers.set('Accept-Language', currentLang).set('Content-Language', currentLang).set('lang', currentLang);
    } else {
      headers = headers.set('Accept', 'image/webp,*/*').delete('content-type');
    }

    request = request.clone({ headers });

    // Check if the request includes custom header to bypass deduplication.
    const bypassDedup = request.headers.has('X-No-Dedup');

    if (bypassDedup) {
      // Optionally remove the header so it doesn't affect server-side logic.
      request = request.clone({ headers: request.headers.delete('X-No-Dedup') });
      return next.handle(request);
    }

    const requestKey = this._getRequestKey(request);
    const ongoingRequest = this._ongoingRequests.get(requestKey);

    if (ongoingRequest) {
      // If a request with the same key is already ongoing, return that observable.
      return ongoingRequest.asObservable();
    } else {
      const cancelSubject = new Subject<any>();
      this._ongoingRequests.set(requestKey, cancelSubject);

      return next.handle(request).pipe(
        takeUntil(cancelSubject), // Cancel the request if needed.
        tap((event) => {
          if (event instanceof HttpResponse && containsDeletedCurrentUserRecord(event.body, this._credentialsService.credentials?.userId)) {
            this._sessionDeletionService.handleDeletedSession();
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401 || error.status === 403) {
            this._credentialsService.setCredentials();
            window.location.href = '/login';
          }
          return throwError(error);
        }),
        finalize(() => {
          this._ongoingRequests.delete(requestKey);
          cancelSubject.complete();
        }),
      );
    }
  }

  private _getRequestKey(req: HttpRequest<any>): string {
    // Use the request method and URL as a unique key
    return `${req.method} ${req.urlWithParams}`;
  }
}
